/**
 * 관리자 "🧸 캐릭터" 화면이 보여주는 **캐릭터 한 명**의 정의 — 단일 정의(FR-ADMIN).
 *
 * 왜 필요한가: 예전에는 한 캐릭터를 고치려면 화면 네 곳을 돌아다녀야 했다.
 *   그림 = [그림 전체 목록], 걷기 = [움직임 만들기], 키 = [캐릭터 크기], 말 = [NPC 대사].
 * 게다가 목록이 화면마다 따로 박혀 있어서 **직접꾸미기 룩 16종은 어느 화면에도 없었다**.
 * 여기서 카탈로그를 기준으로 한 번에 모아, 빠지는 캐릭터가 생기지 않게 한다.
 */
import { HERO_BASE_PRESETS, HERO_CATALOG, customHeroIds, heroBaseKey, heroLookKey } from "./hero";
import { WALK_DIRS } from "./animSlot";

export type CharGroupId = "village" | "hero" | "main";

export interface CharGroup {
  id: CharGroupId;
  icon: string;
  label: string;
  hint: string;
}

export const CHAR_GROUPS: readonly CharGroup[] = [
  { id: "village", icon: "🐣", label: "마을 친구", hint: "체험공간을 돌아다니는 친구들" },
  { id: "hero", icon: "🧑", label: "아이 캐릭터", hint: "아이가 고르거나 꾸미는 모습" },
  { id: "main", icon: "🏠", label: "첫 화면 캐릭터", hint: "홈 화면 무대에 서 있는 친구" },
];

export interface CharDef {
  /** 매니페스트 캐릭터 키 — 걷기는 `<key>.down|.up|.side`. */
  key: string;
  group: CharGroupId;
  label: string;
  /** 정지 그림 슬롯(있는 캐릭터만). NPC·플레이어는 걷기 0번 칸이 곧 정지 모습이라 없다. */
  stillKey?: string;
  /** 정지 그림에 쓸 슬롯 규격 키(content/asset-slots.json). */
  stillSpec?: string;
  /** 방향 걷기 3종을 갖는가. */
  walk: boolean;
  /** 제자리 루프 스트립 키(첫 화면 캐릭터). */
  loopKey?: string;
  /** 크기·대사의 대상이 되는 NPC id (마을 친구만). */
  npcId?: string;
  /** 이 캐릭터가 "내 히어로" 크기를 따르는가 — 크기 탭이 히어로 슬라이더를 보여준다. */
  isHero?: boolean;
}

const OUTFIT_LABEL: Record<string, string> = { o0: "기본 옷", o1: "겨울 옷", o2: "탐험가 옷", o3: "우비" };

/**
 * 캐릭터 전체 목록.
 * @param npcs 마을 친구 로스터(매니페스트에서 발견한 커스텀 NPC 포함)
 * @param mains 첫 화면 무대 캐릭터
 * @param artKeys **그림이 실제로 올라와 있는** 매니페스트 키들. 주면 운영자가 만든 캐릭터가
 *   여기서 발견돼 목록에 붙는다 — 코드에 자리를 뚫어 두지 않으므로 이게 유일한 출처다.
 *   안 주면 기본 제공분만 낸다(그림을 모르는 곳에서 부를 때).
 */
export function charRoster(
  npcs: readonly { id: string; label: string }[],
  mains: readonly { id: string; label: string }[],
  artKeys?: Iterable<string>,
): CharDef[] {
  const out: CharDef[] = [];

  for (const n of npcs) {
    out.push({ key: `npc.${n.id}`, group: "village", label: n.label, walk: true, npcId: n.id });
  }

  out.push({
    key: "avatar.player",
    group: "hero",
    label: "기본 아바타(그림 없을 때)",
    walk: true,
    isHero: true,
  });
  // 기본 제공은 그림이 없어도 남긴다 — 거긴 비어 있으면 그게 진짜 문제라 보여야 한다.
  // 운영자가 만든 것은 **그림에서 발견한다** — 올리면 생기고 지우면 없어진다.
  const made = artKeys ? customHeroIds(artKeys) : [];
  const heroes = [
    ...HERO_BASE_PRESETS.map((p) => ({ id: p.id, label: `완성 캐릭터 · ${p.label}` })),
    ...made.map((id) => ({ id, label: `완성 캐릭터 · ${id}` })),
  ];
  for (const h of heroes) {
    out.push({
      key: heroBaseKey(h.id),
      group: "hero",
      label: h.label,
      stillKey: heroBaseKey(h.id),
      stillSpec: "hero.base",
      walk: true,
      isHero: true,
    });
  }
  for (const b of HERO_CATALOG.body) {
    for (const o of HERO_CATALOG.outfit) {
      const key = heroLookKey(b, o);
      out.push({
        key,
        group: "hero",
        label: `직접꾸미기 · 아이 ${b.slice(1)} · ${OUTFIT_LABEL[o] ?? o}`,
        stillKey: key,
        stillSpec: "hero.base", // 통짜 룩은 프리셋과 같은 규격(투명 정사각)
        walk: true,
        isHero: true,
      });
    }
  }

  for (const m of mains) {
    out.push({
      key: `landing.char.${m.id}`,
      group: "main",
      label: m.label,
      stillKey: `landing.char.${m.id}`,
      stillSpec: "landing.char",
      walk: false,
      loopKey: `landing.char.${m.id}.strip`,
    });
  }

  return out;
}

/** 이 캐릭터가 쓰는 애니 슬롯 키 전부(걷기 3 + 제자리 루프). */
export function charAnimKeys(c: CharDef): string[] {
  const keys = c.walk ? WALK_DIRS.map((d) => `${c.key}.${d}`) : [];
  if (c.loopKey) keys.push(c.loopKey);
  return keys;
}

/** 목록에서 몇 장이 채워졌는지 — "아직 안 만든 캐릭터"를 한눈에 보여주려고. */
export function charFilled(c: CharDef, has: (key: string) => boolean): [number, number] {
  const keys = [...(c.stillKey ? [c.stillKey] : []), ...charAnimKeys(c)];
  return [keys.filter(has).length, keys.length];
}
